import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import CinematicBackdrop from '../cinematic/CinematicBackdrop';
import SplitText from '../cinematic/SplitText';
import MagneticButton from '../cinematic/MagneticButton';

const FAQ = [
  {
    q: 'Is DIS2ORT free?',
    a: 'Yes. You get 50 free credits the moment you sign in — enough for ~5 high-quality images or 1–2 short videos. After that, credit packs start at $0.99 (10 credits) and credits never expire.',
  },
  {
    q: 'How many AI models do you actually have?',
    a: '25 — 15 image models and 10 video models. Image: DALL·E 3 Turbo, Imagen 3, Ideogram 3.0, Recraft V3, Midjourney v7, FLUX.1 Pro/Dev/Schnell, SDXL Turbo, Stable Diffusion 3.5, Leonardo Phoenix, Playground v3, Stable Cascade, AuraFlow v0.2, plus DIS2ORT Studio. Video: Sora 2, Kling 2.0, Luma Dream Machine, Hailuo MiniMax, CogVideoX-5b, SVD-XT, Runway Gen-3, Mochi 1, Hunyuan Video, LTX Video.',
  },
  {
    q: 'What do credits buy?',
    a: 'A typical image generation costs 5 credits. A 4-second Sora 2 video costs 25 credits. Image-to-video animation costs 25–55 credits depending on length. Real-ESRGAN upscale costs 10 credits. Sketch + inpainting costs 5–10 credits.',
  },
  {
    q: 'Why is this cheaper than Midjourney / Runway / Leonardo?',
    a: 'Because we resell GPU compute at wholesale. We don\'t train our own foundation models — we route your prompts to OpenAI, Google, Stability, Black Forest Labs, Tencent, Genmo, and others. You pay only for what you generate, no monthly subscription.',
  },
  {
    q: 'Do I own the images & videos I generate?',
    a: 'Yes. Whatever you create is yours — full commercial license. We don\'t claim any rights. Each underlying model has its own license; DIS2ORT routes you through the appropriate one.',
  },
  {
    q: 'Why does it take 2–5 minutes for video sometimes?',
    a: 'Sora 2 and other large video models are computationally heavy — even at $200B+ in GPU infrastructure, a 4-second clip takes ~3 minutes. We\'ll email you when it\'s ready if you close the tab.',
  },
  {
    q: 'Can I make NSFW content?',
    a: 'No. OpenAI, Google, Stripe, and Cloudflare all explicitly forbid it on their networks. Any account that attempts NSFW will be suspended — and our payment + hosting providers would shut us down within hours. We focus on the legal creative use cases.',
  },
  {
    q: 'How is my data handled?',
    a: 'Your prompts and outputs are stored privately by default. You decide what to publish to the community. We never train any model on your data. Stripe handles all payment info (we never see card numbers).',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'There\'s nothing to cancel — DIS2ORT is pay-as-you-go. No subscription, no auto-renew. Just buy credits when you need them.',
  },
  {
    q: 'Do you have an API?',
    a: 'Not yet. If you have a high-volume use case, email us — we\'ll set up custom access.',
  },
];

export default function FAQPage() {
  const [open, setOpen] = useState(0);
  return (
    <div className="min-h-screen text-white cinematic-grain relative" data-testid="faq-page">
      <CinematicBackdrop intensity="normal" />
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 sm:px-10 pt-20 pb-24 relative z-10">
        <div className="relative text-center mb-12">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#A3FF00] mb-4">Frequently Asked</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight uppercase mb-4 leading-[0.95]">
            <SplitText text="Got questions?" className="block" stagger={0.04} duration={0.7} />
            <span className="italic bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] bg-clip-text text-transparent">
              <SplitText text="We got answers." stagger={0.05} duration={0.8} delay={0.4} />
            </span>
          </h1>
        </div>

        <div className="relative space-y-2" data-testid="faq-list">
          {FAQ.map((item, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={`faq-${item.q.slice(0, 28)}`}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                className={`rounded-2xl border transition-colors overflow-hidden ${isOpen ? 'bg-white/[0.05] border-white/[0.15]' : 'bg-white/[0.02] border-white/[0.06]'}`}
              >
                <button onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-q-${i}`}
                  className="w-full flex items-center justify-between text-left px-5 py-4 hover:bg-white/[0.02] transition-colors">
                  <span className="font-semibold text-white pr-4">{item.q}</span>
                  <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
                    <ChevronDown className="w-4 h-4 text-white/40 shrink-0" />
                  </motion.div>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 text-sm text-white/65 leading-relaxed">{item.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        <div className="relative mt-12 text-center">
          <p className="text-sm text-white/55 mb-4">Still have a question?</p>
          <MagneticButton strength={0.25} radius={100}>
            <Link to="/login">
              <Button className="bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white font-bold uppercase tracking-[0.15em] px-7 py-5 text-xs rounded-full hover:from-[#FF8E72] hover:to-[#FF6B9D] shadow-[0_8px_24px_rgba(255,107,74,0.3)] transition-shadow" data-testid="faq-cta">
                Try it free <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Button>
            </Link>
          </MagneticButton>
        </div>
      </div>
    </div>
  );
}
